import { useEffect, useState } from "react";
import ApiService from "../../services/api.service";
import { coachInviteErrorCopy } from "../../utils/inviteErrorCopy";
import "../client/ClientLayout.css";
import "../coach/CoachPages.css";
import "./LeaguePages.css";

// Coaches tab — every pending coach invite this league has out
// (GET /league/invites?accountType=coach), and the one place to send the kind
// that belongs to no team.
//
// WHY THIS PAGE EXISTS. "Invite a coach who'll bring their own team" is
// POST /league/invites with NO teamId, and the resulting StaffInvites row has
// teamId: null — so LeagueTeamsPage's TeamRow, which is per-team, structurally
// cannot host it. Modelled on LeagueOfficialsPage, which is already exactly
// this shape (pending rows + resend/revoke + an invite modal).
//
// Team-scoped coach invites are shown here TOO, even though they also appear
// inside their TeamRow. That duplication is deliberate: the two answer
// different questions ("who coaches this team" vs "who owes me an answer"),
// and filtering them out would mean an invite silently vanishing from the list
// you were just looking at the moment someone attached it to a team.
//
// Pending AFFILIATIONS deliberately live on the Teams page instead: one of
// those becomes a TEAM, so it belongs where the admin is counting teams.

function InviteCoachModal({ onClose, onSaved }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copiedNotice, setCopiedNotice] = useState(false);

  const canSave = phoneNumber.trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // teamId is OMITTED, and that omission IS the feature: with no teamId,
      // polyp-staff-invite-claim stamps Users.invitedByLeagueId on the coach
      // who claims it, and their own POST /coach/teams then writes an ACTIVE
      // TeamLeagueAffiliations row for this league — no second consent step,
      // because claiming the SMS invite was the consent. Passing a teamId
      // here would quietly turn this into an ordinary per-team coach invite
      // onto a league-owned team, which is a different product behaviour.
      const resp = await ApiService.createLeagueInvite({
        accountType: "coach",
        phoneNumber: phoneNumber.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      if (resp.smsSent === false && resp.inviteUrl) {
        await navigator.clipboard?.writeText(resp.inviteUrl).catch(() => {});
        setCopiedNotice(true);
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError(coachInviteErrorCopy(err));
      setSaving(false);
    }
  };

  if (copiedNotice) {
    return (
      <div className="coach-modal-backdrop" onClick={onClose}>
        <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="client-h2">Couldn't text the coach</h2>
          <p className="client-subtitle">
            The invite link couldn't be delivered by SMS, so we copied it to your clipboard — share it with them yourself.
          </p>
          <div className="coach-modal-actions">
            <button className="client-button" onClick={onSaved}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">Invite Coach</h2>
        <div className="coach-modal-fields">
          <input className="client-input" placeholder="First name" value={firstName}
            onChange={(e) => setFirstName(e.target.value)} />
          <input className="client-input" placeholder="Last name" value={lastName}
            onChange={(e) => setLastName(e.target.value)} />
          <input className="client-input" placeholder="Phone number" value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)} />
        </div>
        <p className="client-subtitle">
          For a coach whose team isn't on Polyp yet. They'll set up their own login
          and create the team with its own #hashtag — it joins your league
          automatically. They own the team; you schedule it.
        </p>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="client-button" onClick={save} disabled={saving || !canSave}>
            {saving ? "Sending…" : "Invite Coach"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CoachInviteRow({ invite, teamName, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const resend = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await ApiService.resendLeagueInvite(invite.inviteId);
      if (resp.smsSent === false && resp.inviteUrl) {
        await navigator.clipboard?.writeText(resp.inviteUrl).catch(() => {});
        window.alert("Couldn't text the coach — the invite link was copied to your clipboard instead.");
      }
    } catch (err) {
      setError(coachInviteErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this invite?")) return;
    setBusy(true);
    setError(null);
    try {
      await ApiService.revokeLeagueInvite(invite.inviteId);
      onChanged();
    } catch (err) {
      setError(coachInviteErrorCopy(err));
      setBusy(false);
    }
  };

  return (
    <div className="coach-history-card">
      <div className="coach-history-row" style={{ cursor: "default" }}>
        <div>
          <div className="coach-history-matchup">
            {`${invite.firstName || ""} ${invite.lastName || ""}`.trim() || invite.phoneNumber}
          </div>
          <div className="coach-history-date">
            {invite.phoneNumber} · {invite.teamId ? (teamName || "Team invite") : "Bringing their own team"}
          </div>
        </div>
        <div className="league-invite-actions">
          <button className="client-button-secondary" onClick={resend} disabled={busy}>Resend</button>
          <button className="league-remove-team" onClick={revoke} disabled={busy}>Revoke</button>
        </div>
      </div>
      {error && <div className="client-error">{error}</div>}
    </div>
  );
}

export default function LeagueCoachesPage() {
  const [invites, setInvites] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [invitesResp, teamsResp] = await Promise.all([
        ApiService.getLeagueInvites("coach"),
        ApiService.getLeagueTeams(),
      ]);
      setInvites((invitesResp.invites || []).filter((i) => i.status === "pending"));
      setTeams(teamsResp.teams || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamNameFor = (teamId) => teams.find((t) => t.teamId === teamId)?.name;

  const coachedTeams = teams.filter((t) => t.inviteStatus === "claimed");

  return (
    <div>
      <div className="coach-page-header">
        <h1 className="client-h1">Coaches</h1>
        <button className="client-button" onClick={() => setShowInvite(true)}>+ Invite Coach</button>
      </div>

      {error && <div className="client-error">{error}</div>}

      {loading ? (
        <div className="client-loading-row">Loading…</div>
      ) : (
        <>
          {invites.length > 0 && (
            <>
              <p className="client-subtitle">Pending invites</p>
              <div className="coach-history-list">
                {invites.map((inv) => (
                  <CoachInviteRow
                    key={inv.inviteId}
                    invite={inv}
                    teamName={teamNameFor(inv.teamId)}
                    onChanged={load}
                  />
                ))}
              </div>
            </>
          )}

          {coachedTeams.length === 0 && invites.length === 0 ? (
            <div className="client-empty">
              No coaches yet. Invite one above — either onto a team you've created, or to bring their own.
            </div>
          ) : coachedTeams.length > 0 ? (
            <>
              <p className="client-subtitle">Coaching</p>
              <div className="coach-history-list">
                {coachedTeams.map((t) => (
                  <div key={t.teamId} className="coach-history-card">
                    <div className="coach-history-row" style={{ cursor: "default" }}>
                      <div>
                        <div className="coach-history-matchup">{t.coachEmail || "Coach"}</div>
                        <div className="coach-history-date">{t.name || "Unnamed Team"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      )}

      {showInvite && (
        <InviteCoachModal
          onClose={() => setShowInvite(false)}
          onSaved={() => { setShowInvite(false); load(); }}
        />
      )}
    </div>
  );
}
